/**
 * AdminLayout.tsx — Application shell for the billing admin console
 *
 * Purpose:
 *   Provides the top-level page layout: sidebar navigation, header with
 *   logout button, and main content area. All protected admin pages render
 *   their content as children inside this shell.
 *
 * Public API:
 *   - AdminLayout  Layout wrapper component
 *
 * @module packages/billing-admin/src/components/AdminLayout
 * @since 2026-05-31 (AT-50.01 — Scaffold Admin Console UI Foundation)
 */
import React, { useState, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useOrgContext } from '../contexts/OrgContext';
import OrgSwitcher from './OrgSwitcher';
import SettingsDropdown from './SettingsDropdown';
import { GuideToggleButton, IntegrationGuidePanel } from './IntegrationGuide';
import type { GuideContent } from './IntegrationGuide';
import {
  overviewGuide,
  usagePricingGuide,
  customersGuide,
  creditsGuide,
  webhooksGuide,
  plansGuide,
  subscriptionsGuide,
  invoicesGuide,
  revenueGuide,
  auditLogGuide,
  userBudgetsGuide,
  contractsGuide,
  reconciliationGuide,
  operatorsGuide,
  pricingPagesGuide,
} from '../guides/guideContent';

const ROUTE_GUIDES: Record<string, GuideContent> = {
  '/overview': overviewGuide,
  '/usage-pricing': usagePricingGuide,
  '/customers': customersGuide,
  '/credits': creditsGuide,
  '/webhooks': webhooksGuide,
  '/plans': plansGuide,
  '/subscriptions': subscriptionsGuide,
  '/invoices': invoicesGuide,
  '/revenue': revenueGuide,
  '/audit-log': auditLogGuide,
  '/user-budgets': userBudgetsGuide,
  '/contracts': contractsGuide,
  '/reconciliation': reconciliationGuide,
  '/operators': operatorsGuide,
  '/pricing-pages': pricingPagesGuide,
  '/marketing-features': pricingPagesGuide,
};

/** Navigation entry for the sidebar. */
interface NavItem {
  label: string;
  to: string;
}

interface NavGroup {
  heading: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    heading: 'Dashboards',
    items: [
      { label: 'Overview', to: '/overview' },
      { label: 'Revenue', to: '/revenue' },
      { label: 'Event Log', to: '/usage-events' },
    ],
  },
  {
    heading: 'Applications',
    items: [
      { label: 'All Apps', to: '/applications' },
    ],
  },
  {
    heading: 'Customers',
    items: [
      { label: 'Accounts', to: '/customers' },
      { label: 'Subscriptions', to: '/subscriptions' },
      { label: 'Invoices', to: '/invoices' },
      { label: 'Credits', to: '/credits' },
      { label: 'Contracts', to: '/contracts' },
    ],
  },
  {
    heading: 'Catalog',
    items: [
      { label: 'Plans', to: '/plans' },
      { label: 'Billing as Code', to: '/billing-config' },
      { label: 'Marketing Features', to: '/marketing-features' },
      { label: 'Pricing Pages', to: '/pricing-pages' },
      { label: 'Usage Pricing', to: '/usage-pricing' },
      { label: 'Event Types', to: '/event-types' },
      { label: 'User Budgets', to: '/user-budgets' },
    ],
  },
  {
    heading: 'Team',
    items: [
      { label: 'Members', to: '/members' },
    ],
  },
  {
    heading: 'Operations',
    items: [
      { label: 'Reconciliation', to: '/reconciliation' },
      { label: 'Webhooks & Sync', to: '/webhooks' },
      { label: 'Event Catalog', to: '/webhook-events' },
      { label: 'Automation Rules', to: '/automation-rules' },
      { label: 'Email Templates', to: '/email-templates' },
      { label: 'Audit Log', to: '/audit-log' },
    ],
  },

];

interface AdminLayoutProps {
  /** Page content rendered in the main area. */
  children: React.ReactNode;
  /** Optional page title shown in the header. */
  title?: string;
}

/**
 * AdminLayout — renders the sidebar + header + main content shell.
 *
 * @param props.children - Page-specific content
 * @param props.title    - Page title for the header bar
 * @since 2026-05-31 (AT-50.01)
 */
export default function AdminLayout({
  children,
  title,
}: AdminLayoutProps): React.JSX.Element {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Resolve guide content from current route
  const guideKey = Object.keys(ROUTE_GUIDES).find((key) => location.pathname.startsWith(key));
  const currentGuide = guideKey ? ROUTE_GUIDES[guideKey] : overviewGuide;

  // Auto-expand the group containing the active route
  const activeGroup = NAV_GROUPS.find((g) =>
    g.items.some((item) => location.pathname === item.to || location.pathname.startsWith(item.to + '/'))
  );
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (activeGroup) initial.add(activeGroup.heading);
    return initial;
  });

  const toggleGroup = useCallback((heading: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(heading)) next.delete(heading);
      else next.add(heading);
      return next;
    });
  }, []);

  function handleLogout(): void {
    void logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Sidebar */}
      <div className="flex min-h-screen">
      <aside className="flex w-64 flex-shrink-0 flex-col border-r border-white/10 bg-slate-950/80 text-white backdrop-blur">
        <div className="border-b border-white/10 px-5 py-6">
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-cyan-300/80">Rule42 Billing</p>
          <span className="mt-2 block text-xl font-semibold text-white">Admin Console</span>
          <p className="mt-2 text-sm text-slate-400">Operate plans, billing accounts, subscriptions, contracts, and reconciliation from one control surface.</p>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {NAV_GROUPS.map((group) => {
            const isExpanded = expandedGroups.has(group.heading);
            const hasActiveItem = group.items.some(
              (item) => location.pathname === item.to || location.pathname.startsWith(item.to + '/')
            );
            return (
            <div key={group.heading} className="mb-1">
              <button
                onClick={() => toggleGroup(group.heading)}
                className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors ${
                  hasActiveItem
                    ? 'text-cyan-300'
                    : 'text-slate-300 hover:text-white'
                }`}
              >
                <svg
                  className={`h-3 w-3 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                  fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
                <span>{group.heading}</span>
              </button>
              {isExpanded && (
                <div className="ml-3 space-y-0.5 pb-1">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `block rounded-lg px-4 py-2 text-sm transition-colors ${
                          isActive
                            ? 'bg-cyan-400/15 font-medium text-white shadow-[inset_0_0_0_1px_rgba(103,232,249,0.24)]'
                            : 'text-slate-300 hover:bg-white/8 hover:text-white'
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
            );
          })}
        </nav>
        <div className="border-t border-white/10 px-5 py-4 text-xs text-slate-400">
          <div className="rounded-xl bg-white/5 px-3 py-3">
            <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Signed in as</p>
            <p className="mt-1 truncate text-sm text-slate-200">{user?.email ?? ''}</p>
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-200 bg-white px-8 py-5 shadow-sm">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-700">Billing control plane</p>
            <h1 className="mt-1 text-xl font-semibold text-slate-950">{title ?? 'Admin'}</h1>
          </div>
          <div className="flex items-center gap-3">
            <OrgSwitcher />
            <SettingsDropdown />
            <GuideToggleButton />
            <button
              onClick={handleLogout}
              className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-950"
            >
              Logout
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto bg-slate-50 px-8 py-8">{children}</main>

        {/* Footer */}
        <footer className="border-t border-slate-200 bg-white px-8 py-4">
          <div className="flex items-center justify-between text-xs text-slate-400">
            <div className="flex items-center gap-4">
              <span className="font-medium text-slate-500">Powered by <a href="https://rule42.ai" target="_blank" rel="noopener noreferrer" className="text-cyan-700 hover:text-cyan-900 transition">RULE42</a></span>
              <span>© {new Date().getFullYear()} Rule42. All rights reserved.</span>
            </div>
            <div className="flex items-center gap-4">
              <a href="https://rule42.ai/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 transition">Privacy Policy</a>
              <a href="https://rule42.ai/terms" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 transition">Terms of Service</a>
              <a href="https://docs.rule42.ai" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 transition">Documentation</a>
            </div>
          </div>
        </footer>
      </div>
      </div>

      {/* Integration Guide Panel */}
      <IntegrationGuidePanel guide={currentGuide} />
    </div>
  );
}
